import type { KeyboardEvent, PointerEvent } from 'react';
import { Box } from '@mui/material';

type DialogResizeEdge = 'n' | 's' | 'e' | 'w' | 'se';

/** How far a centered edge follows the pointer. Left and top grow when the pointer moves toward them. */
const DIALOG_RESIZE_FACTOR: Record<DialogResizeEdge, { x: number; y: number; cursor: string }> = {
  n: { x: 0, y: -2, cursor: 'ns-resize' },
  s: { x: 0, y: 2, cursor: 'ns-resize' },
  e: { x: 2, y: 0, cursor: 'ew-resize' },
  w: { x: -2, y: 0, cursor: 'ew-resize' },
  se: { x: 2, y: 2, cursor: 'nwse-resize' },
};

const edgePosition: Record<DialogResizeEdge, Record<string, number | string>> = {
  n: { top: 0, left: 8, right: 48, height: 8 },
  s: { bottom: 0, left: 8, right: 8, height: 8 },
  e: { top: 8, bottom: 8, right: 0, width: 8 },
  w: { top: 8, bottom: 8, left: 0, width: 8 },
  se: { right: 0, bottom: 0, width: 16, height: 16 },
};

interface SidePanelResizeProps {
  label: string;
  valueText: string;
  valueNow: number;
  valueMin: number;
  valueMax: number;
  testId?: string;
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

export function WorkOrderCreateSidePanelResize({
  label,
  valueText,
  valueNow,
  valueMin,
  valueMax,
  testId = 'create-wizard-side-panel-resize',
  onPointerDown,
  onKeyDown,
}: SidePanelResizeProps) {
  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuetext={valueText}
      aria-valuemin={valueMin}
      aria-valuemax={valueMax}
      aria-valuenow={valueNow}
      tabIndex={0}
      data-testid={testId}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      sx={{
        width: 12,
        flexShrink: 0,
        cursor: 'col-resize',
        touchAction: 'none',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: '50%',
          width: '1px',
          bgcolor: 'divider',
        },
        '&:hover::before, &:focus-visible::before': {
          width: 2,
          bgcolor: 'primary.main',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: -2,
        },
      }}
    />
  );
}

interface DialogResizeFactor {
  x: number;
  y: number;
  cursor: string;
}

interface DialogResizeProps {
  label: string;
  valueText: string;
  onEdgePointerDown: (factor: DialogResizeFactor, event: PointerEvent<HTMLElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

function ResizeEdge({
  edge,
  onPointerDown,
}: {
  edge: Exclude<DialogResizeEdge, 'se'>;
  onPointerDown: (factor: DialogResizeFactor, event: PointerEvent<HTMLElement>) => void;
}) {
  return (
    <Box
      role="presentation"
      onPointerDown={(event) => { onPointerDown(DIALOG_RESIZE_FACTOR[edge], event); }}
      sx={{
        position: 'absolute',
        zIndex: 2,
        touchAction: 'none',
        cursor: DIALOG_RESIZE_FACTOR[edge].cursor,
        ...edgePosition[edge],
      }}
    />
  );
}

export function WorkOrderCreateDialogResize({
  label,
  valueText,
  onEdgePointerDown,
  onKeyDown,
}: DialogResizeProps) {
  return (
    <>
      <ResizeEdge edge="n" onPointerDown={onEdgePointerDown} />
      <ResizeEdge edge="s" onPointerDown={onEdgePointerDown} />
      <ResizeEdge edge="e" onPointerDown={onEdgePointerDown} />
      <ResizeEdge edge="w" onPointerDown={onEdgePointerDown} />
      <Box
        component="button"
        type="button"
        aria-label={label}
        aria-valuetext={valueText}
        data-testid="create-wizard-dialog-resize"
        onPointerDown={(event) => { onEdgePointerDown(DIALOG_RESIZE_FACTOR.se, event); }}
        onKeyDown={onKeyDown}
        sx={{
          position: 'absolute',
          zIndex: 2,
          p: 0,
          border: 0,
          bgcolor: 'transparent',
          touchAction: 'none',
          cursor: DIALOG_RESIZE_FACTOR.se.cursor,
          ...edgePosition.se,
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: -2,
          },
        }}
      />
    </>
  );
}
